import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import { api, API_BASE_URL } from '@/services/api';
import SkoRedesign from './SkoRedesign';

// =============================================================================
// Types
// =============================================================================
interface PrsSubmission {
  id: string;
  assetId: string;
  assetName: string;
  scheduleDate: string;
  revisionNumber: number;
  mrid: string;
  senderXCode: string;
  registeredWCode: string;
  processType: string;
  targetEndpoint: string;
  pointsCount: number;
  totalEnergyMwh: number | null;
  status: string;
  submittedAt: string | null;
  sourceModelVersion: string;
  sourceForecastRunAt: string | null;
  xmlBytes: number;
  createdAt: string;
}

interface CurtailmentEvent {
  id: string;
  assetId: string;
  assetName: string;
  installedCapacityAcMw: number | null;
  eventKind: string;
  source: string | null;
  startedAtLocal: string;
  endedAtLocal: string | null;
  curtailmentPct: number | null;
  curtailmentPmaxKw: number | null;
  reason: string | null;
  dispatcherName: string | null;
  dispatcherOrg: string | null;
  notes: string | null;
  actualCompliancePct: number | null;
  relatedGridLoss: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AssetOption {
  id: string;
  assetName: string;
  xCode: string;
  wCode: string;
  installedCapacityAcMw: number | null;
}

const fmtDate = (d: string | null): string => {
  if (!d) return '—';
  return d.length >= 10 ? d.slice(0, 10) : d;
};
const fmtDateTime = (d: string | null): string => {
  if (!d) return '—';
  return d.replace('T', ' ').slice(0, 16);
};
const fmtNum = (v: any, digits = 0): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

type SectionKey = 'inbox' | 'prs' | 'curtailment' | 'about';

// =============================================================================
/**
 * Дуальний режим (§6 handoff): стенд із VITE_REDESIGN бачить редизайн, прод —
 * незмінений SkoPageLegacy нижче. Пункт меню /sko не має feature-гейта, тож
 * прод цю сторінку показує, і ламати її не можна.
 */
export default function SkoPage() {
  return import.meta.env.VITE_REDESIGN === '1' ? <SkoRedesign /> : <SkoPageLegacy />;
}

function SkoPageLegacy() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [section, setSection] = useState<SectionKey>('inbox');

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';

  const sections = [
    { key: 'inbox' as const,       label: t('sko_section_inbox'),       icon: 'ri-inbox-line' },
    { key: 'prs' as const,         label: t('sko_section_prs'),         icon: 'ri-send-plane-line' },
    { key: 'curtailment' as const, label: t('sko_section_curtailment'), icon: 'ri-stop-circle-line' },
    { key: 'about' as const,       label: t('sko_section_about'),       icon: 'ri-information-line' },
  ];

  return (
    <DashboardLayout>
      <div className="px-6 py-6 w-full">
        <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: textMain }}>
              {t('sko_page_title')}
            </h1>
            <p className="text-sm mt-1" style={{ color: textMuted }}>{t('sko_page_subtitle')}</p>
          </div>
          <a
            href="https://ua.energy/uchasnikam-rinku/vidnovlyuvani-dzherela-energiyi/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs hover:underline cursor-pointer whitespace-nowrap"
            style={{ color: textMuted }}
          >
            {t('sko_external_link')} ↗
          </a>
        </div>

        {/* Browser-style tab strip */}
        <div
          className="flex items-end gap-1 overflow-x-auto"
          style={{ borderBottom: `1px solid ${cardBorder}` }}
        >
          {sections.map((s) => {
            const isActive = section === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setSection(s.key)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm whitespace-nowrap cursor-pointer transition"
                style={{
                  background: isActive ? cardBg : 'transparent',
                  color: isActive ? 'var(--accent)' : textMuted,
                  fontWeight: isActive ? 600 : 400,
                  borderTop: `1px solid ${isActive ? cardBorder : 'transparent'}`,
                  borderLeft: `1px solid ${isActive ? cardBorder : 'transparent'}`,
                  borderRight: `1px solid ${isActive ? cardBorder : 'transparent'}`,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  marginBottom: isActive ? -1 : 0,
                  position: 'relative',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#F3F4F6'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <i className={s.icon}></i>
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Tab content — full width */}
        <div
          className="w-full min-w-0 p-5 rounded-b-lg rounded-tr-lg"
          style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            borderTop: 'none',
          }}
        >
          {section === 'prs' && <PrsSection t={t} isDark={isDark} />}
          {section === 'curtailment' && <CurtailmentSection t={t} isDark={isDark} />}
          {section === 'about' && <AboutSection t={t} isDark={isDark} />}
          {section === 'inbox' && <InboxSection t={t} isDark={isDark} />}
        </div>
      </div>
    </DashboardLayout>
  );
}

// =============================================================================
// PRS submissions section
// =============================================================================
function PrsSection({ t, isDark }: { t: (k: string) => string; isDark: boolean }) {
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB';
  const tableBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const selectBg = isDark ? '#1A1A1A' : '#fff';
  const [items, setItems] = useState<PrsSubmission[]>([]);
  const [selected, setSelected] = useState<PrsSubmission | null>(null);
  const [xmlPreview, setXmlPreview] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<{ submissions: PrsSubmission[] }>(`/sko/prs/list?days=${days}`)
      .then((r) => { if (!cancelled) setItems(r.submissions); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  const openPreview = async (item: PrsSubmission) => {
    setSelected(item);
    setXmlPreview(null);
    try {
      const r = await api.get<{ xmlPayload: string }>(`/sko/prs/${item.id}`);
      setXmlPreview(r.xmlPayload);
    } catch (e: any) {
      setXmlPreview(`<error: ${e?.message ?? 'failed'}>`);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <i className="ri-information-line text-blue-600 mt-0.5"></i>
        <div className="text-sm text-blue-900">
          <strong>{t('sko_prs_banner_title')}.</strong> {t('sko_prs_banner_text')}
        </div>
      </div>

      <div className="rounded-lg p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold" style={{ color: textMain }}>
            {t('sko_prs_table_title')} ({items.length})
          </h3>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
          >
            <option value={7}>7 {t('ueex_page_days')}</option>
            <option value={14}>14 {t('ueex_page_days')}</option>
            <option value={30}>30 {t('ueex_page_days')}</option>
            <option value={90}>90 {t('ueex_page_days')}</option>
          </select>
        </div>

        {error && <div className="rounded p-2 mb-2 text-sm bg-[color-mix(in_srgb,var(--err)_10%,transparent)] border border-[color-mix(in_srgb,var(--err)_30%,transparent)] text-[var(--err)]">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: tableHeaderBg }}>
              <tr className="border-b" style={{ borderColor: tableBorder }}>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_prs_col_date')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_prs_col_asset')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">mRID</th>
                <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('sko_prs_col_rev')}</th>
                <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('sko_prs_col_points')}</th>
                <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('sko_prs_col_energy')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_prs_col_status')}</th>
                <th className="py-2 px-2 font-medium text-gray-600 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-sm text-gray-400">{t('sko_no_data')}</td></tr>
              )}
              {items.map((s) => (
                <tr key={s.id} className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5">
                  <td className="py-2 px-2 font-mono text-xs">{fmtDate(s.scheduleDate)}</td>
                  <td className="py-2 px-2">{s.assetName}</td>
                  <td className="py-2 px-2 font-mono text-[11px]">{s.mrid}</td>
                  <td className="py-2 px-2 text-right font-mono">{s.revisionNumber}</td>
                  <td className="py-2 px-2 text-right font-mono">{s.pointsCount}</td>
                  <td className="py-2 px-2 text-right font-mono">{fmtNum(s.totalEnergyMwh, 2)} МВт·год</td>
                  <td className="py-2 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      s.status === 'dry_run' ? 'bg-gray-100 text-gray-700' :
                      s.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                      s.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      s.status === 'rejected' ? 'bg-[color-mix(in_srgb,var(--err)_14%,transparent)] text-[var(--err)]' : 'bg-yellow-100 text-yellow-700'
                    }`}>{s.status}</span>
                  </td>
                  <td className="py-2 px-2 text-right">
                    <button
                      type="button"
                      onClick={() => openPreview(s)}
                      className="text-xs text-blue-600 hover:underline cursor-pointer mr-2"
                    >
                      {t('sko_prs_preview')}
                    </button>
                    <a
                      href={`${API_BASE_URL}/sko/prs/${s.id}/download.xml`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline cursor-pointer"
                    >
                      XML ↓
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="rounded-lg p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold" style={{ color: textMain }}>
              {t('sko_prs_preview_title')}: <span className="font-mono text-xs text-gray-500">{selected.mrid}</span>
            </h3>
            <button
              type="button"
              onClick={() => { setSelected(null); setXmlPreview(null); }}
              className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-300 cursor-pointer"
            >
              {t('sko_close')} ✕
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
            <KvPair label={t('sko_prs_col_asset')} value={selected.assetName} isDark={isDark} />
            <KvPair label={t('sko_prs_col_date')} value={fmtDate(selected.scheduleDate)} isDark={isDark} />
            <KvPair label="X-код" value={selected.senderXCode} isDark={isDark} />
            <KvPair label="W-код" value={selected.registeredWCode} isDark={isDark} />
            <KvPair label="processType" value={selected.processType} isDark={isDark} />
            <KvPair label={t('sko_prs_col_energy')} value={`${fmtNum(selected.totalEnergyMwh, 2)} МВт·год`} isDark={isDark} />
            <KvPair label={t('sko_prs_col_points')} value={`${selected.pointsCount}`} isDark={isDark} />
            <KvPair label="endpoint" value={selected.targetEndpoint} isDark={isDark} />
          </div>
          <div className="bg-gray-900 rounded p-3 overflow-x-auto">
            <pre className="text-[11px] text-green-300 font-mono leading-tight" style={{ whiteSpace: 'pre' }}>
              {xmlPreview ?? t('sko_loading')}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function KvPair({ label, value, isDark }: { label: string; value: string; isDark: boolean }) {
  return (
    <div className="rounded p-2" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB' }}>
      <div className="text-[10px] uppercase" style={{ color: isDark ? '#6B6B70' : '#6B6B70' }}>{label}</div>
      <div className="font-mono text-xs mt-0.5 truncate" title={value} style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>{value}</div>
    </div>
  );
}

// =============================================================================
// Curtailment events section
// =============================================================================
function CurtailmentSection({ t, isDark }: { t: (k: string) => string; isDark: boolean }) {
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB';
  const tableBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const selectBg = isDark ? '#1A1A1A' : '#fff';
  const [events, setEvents] = useState<CurtailmentEvent[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    api.get<{ events: CurtailmentEvent[] }>('/sko/curtailment-events?days=90')
      .then((r) => setEvents(r.events))
      .catch((e) => setError(e?.message ?? 'load failed'));
  };

  useEffect(() => {
    reload();
    api.get<{ assets: AssetOption[] }>('/sko/assets')
      .then((r) => setAssets(r.assets))
      .catch(() => {});
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
        <i className="ri-lightbulb-line text-yellow-600 mt-0.5"></i>
        <div className="text-sm text-yellow-900">
          <strong>{t('sko_curt_banner_title')}.</strong> {t('sko_curt_banner_text')}
        </div>
      </div>

      <div className="bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold" style={{ color: textMain }}>
            {t('sko_curt_events_title')} ({events.length})
          </h3>
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="text-sm px-3 py-1.5 rounded cursor-pointer transition"
            style={{ background: showForm ? (isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6') : '#0066FF', color: showForm ? textMain : 'white' }}
          >
            {showForm ? `✕ ${t('sko_close')}` : `+ ${t('sko_curt_add_event')}`}
          </button>
        </div>

        {error && <div className="rounded p-2 mb-2 text-sm bg-[color-mix(in_srgb,var(--err)_10%,transparent)] border border-[color-mix(in_srgb,var(--err)_30%,transparent)] text-[var(--err)]">{error}</div>}

        {showForm && (
          <EventForm assets={assets} t={t} onCreated={() => { reload(); setShowForm(false); }} />
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: tableHeaderBg }}>
              <tr className="border-b" style={{ borderColor: tableBorder }}>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_curt_col_asset')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_curt_col_start')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_curt_col_end')}</th>
                <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('sko_curt_col_pct')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_curt_col_reason')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_curt_col_source')}</th>
                <th className="py-2 px-2 font-medium text-gray-600">{t('sko_curt_col_dispatcher')}</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-sm text-gray-400">{t('sko_no_data')}</td></tr>
              )}
              {events.map((e) => (
                <tr key={e.id} className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5">
                  <td className="py-2 px-2">
                    {e.assetName}
                    {e.relatedGridLoss && (
                      <span className="ml-1 text-[10px] bg-orange-100 text-orange-700 px-1 rounded">grid-loss</span>
                    )}
                  </td>
                  <td className="py-2 px-2 font-mono text-xs">{fmtDateTime(e.startedAtLocal)}</td>
                  <td className="py-2 px-2 font-mono text-xs">
                    {e.endedAtLocal ? fmtDateTime(e.endedAtLocal) :
                      <span className="text-green-600">● {t('sko_curt_active')}</span>}
                  </td>
                  <td className="py-2 px-2 text-right font-mono">
                    {e.curtailmentPct !== null ? `${e.curtailmentPct}%` :
                     e.curtailmentPmaxKw !== null ? `${fmtNum(e.curtailmentPmaxKw, 0)} kW` : '—'}
                  </td>
                  <td className="py-2 px-2 text-xs">{e.reason || '—'}</td>
                  <td className="py-2 px-2 text-xs">{e.source || '—'}</td>
                  <td className="py-2 px-2 text-xs">{e.dispatcherName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EventForm({ assets, t, onCreated }: { assets: AssetOption[]; t: (k: string) => string; onCreated: () => void }) {
  const [form, setForm] = useState({
    assetId: '',
    source: 'phone_dispatcher',
    startedAtLocal: new Date().toISOString().slice(0, 16),
    endedAtLocal: '',
    curtailmentPct: '',
    reason: '',
    dispatcherName: '',
    dispatcherOrg: 'Укренерго ОЕС',
    notes: '',
    relatedGridLoss: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.assetId) { setErr('Виберіть СЕС'); return; }
    setSaving(true);
    setErr(null);
    try {
      await api.post('/sko/curtailment-events', {
        ...form,
        curtailmentPct: form.curtailmentPct ? Number(form.curtailmentPct) : null,
        endedAtLocal: form.endedAtLocal || null,
      });
      onCreated();
    } catch (e: any) {
      setErr(e?.message ?? 'failed to save');
    } finally {
      setSaving(false);
    }
  };

  const upd = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={submit} className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-3 mb-3">
      {err && <div className="rounded p-2 mb-2 text-sm bg-[color-mix(in_srgb,var(--err)_10%,transparent)] border border-[color-mix(in_srgb,var(--err)_30%,transparent)] text-[var(--err)]">{err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] text-gray-600 block mb-1">{t('sko_curt_col_asset')} *</label>
          <select
            value={form.assetId}
            onChange={(e) => upd('assetId', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
            required
          >
            <option value="">— виберіть —</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>{a.assetName} ({a.installedCapacityAcMw} МВт)</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[11px] text-gray-600 block mb-1">{t('sko_curt_col_source')}</label>
          <select
            value={form.source}
            onChange={(e) => upd('source', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
          >
            <option value="phone_dispatcher">Телефон диспетчера</option>
            <option value="telegram">Telegram</option>
            <option value="email">Email</option>
            <option value="sko_api">СКО API</option>
            <option value="other">Інше</option>
          </select>
        </div>

        <div>
          <label className="text-[11px] text-gray-600 block mb-1">{t('sko_curt_col_dispatcher')} орг.</label>
          <input
            type="text"
            value={form.dispatcherOrg}
            onChange={(e) => upd('dispatcherOrg', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
          />
        </div>

        <div>
          <label className="text-[11px] text-gray-600 block mb-1">{t('sko_curt_col_start')} *</label>
          <input
            type="datetime-local"
            value={form.startedAtLocal}
            onChange={(e) => upd('startedAtLocal', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
            required
          />
        </div>

        <div>
          <label className="text-[11px] text-gray-600 block mb-1">{t('sko_curt_col_end')}</label>
          <input
            type="datetime-local"
            value={form.endedAtLocal}
            onChange={(e) => upd('endedAtLocal', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
            placeholder="лишити порожнім якщо ще триває"
          />
        </div>

        <div>
          <label className="text-[11px] text-gray-600 block mb-1">Curtailment % (0-100)</label>
          <input
            type="number" min="0" max="100" step="1"
            value={form.curtailmentPct}
            onChange={(e) => upd('curtailmentPct', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
            placeholder="напр. 30 = до 30% від nameplate"
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] text-gray-600 block mb-1">{t('sko_curt_col_reason')}</label>
          <input
            type="text"
            value={form.reason}
            onChange={(e) => upd('reason', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
            placeholder="напр. Перевантаження ЛЕП, Аварія на ПС"
          />
        </div>

        <div>
          <label className="text-[11px] text-gray-600 block mb-1">{t('sko_curt_col_dispatcher')} ім'я</label>
          <input
            type="text"
            value={form.dispatcherName}
            onChange={(e) => upd('dispatcherName', e.target.value)}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
          />
        </div>

        <div className="md:col-span-3">
          <label className="text-[11px] text-gray-600 block mb-1">Нотатки</label>
          <textarea
            value={form.notes}
            onChange={(e) => upd('notes', e.target.value)}
            rows={2}
            className="w-full text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5"
          />
        </div>

        <div className="md:col-span-3 flex items-center gap-2">
          <input
            type="checkbox"
            id="grid_loss_flag"
            checked={form.relatedGridLoss}
            onChange={(e) => upd('relatedGridLoss', e.target.checked)}
          />
          <label htmlFor="grid_loss_flag" className="text-xs text-gray-700">
            Подія пов'язана з grid-loss (anti-islanding), а не з командою диспетчера
          </label>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-1.5 rounded cursor-pointer transition disabled:opacity-50"
          style={{ background: '#0066FF', color: 'white' }}
        >
          {saving ? t('sko_saving') : t('sko_save')}
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// About section
// =============================================================================
function AboutSection({ t, isDark }: { t: (k: string) => string; isDark: boolean }) {
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  return (
    <div className="rounded-lg p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <h3 className="font-semibold mb-3 text-lg" style={{ color: textMain }}>{t('sko_about_title')}</h3>
      <div className="text-sm space-y-3" style={{ color: textMuted }}>
        <p>{t('sko_about_p1')}</p>
        <p>{t('sko_about_p2')}</p>
        <p>{t('sko_about_p3')}</p>
        <div className="rounded p-3" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
          <div className="text-xs font-semibold mb-2">{t('sko_about_phase_status')}</div>
          <ul className="text-xs space-y-1">
            <li>✅ <strong>Phase A</strong>: PRS dry-run generator + curtailment events log (поточна)</li>
            <li>⏳ <strong>Phase B</strong>: договір з ОСП, VPN, отримання production credentials</li>
            <li>⏳ <strong>Phase C</strong>: telemetry IEC 60870-5-104 → ОСП</li>
            <li>⏳ <strong>Phase D</strong>: прийом curtailment команд + автоматичне виконання</li>
          </ul>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// Inbox section — full SKO commands list with filters
// =============================================================================
interface SkoCmd {
  id: string;
  commandType: string;
  severity: string;
  assetId: string | null;
  assetName: string | null;
  receivedVia: string;
  dispatcherName: string | null;
  receivedAt: string;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  ackDeadline: string | null;
  curtailmentPct: number | null;
  targetKw: number | null;
  reason: string | null;
  status: string;
  actualCompliancePct: number | null;
}

function InboxSection({ t, isDark }: { t: (k: string) => string; isDark: boolean }) {
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB';
  const tableBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const selectBg = isDark ? '#1A1A1A' : '#fff';
  const [items, setItems] = useState<SkoCmd[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [days, setDays] = useState(30);
  const [acking, setAcking] = useState<string | null>(null);

  const load = () => {
    const q = statusFilter === 'all' ? '' : `&status=${statusFilter}`;
    api.get<{ commands: SkoCmd[] }>(`/sko/commands?days=${days}${q}&limit=200`)
      .then((r) => { setItems(r.commands); setError(null); })
      .catch((e) => setError(e?.message ?? 'load failed'));
  };

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [statusFilter, days]);

  const handleAck = async (id: string) => {
    setAcking(id);
    try { await api.post(`/sko/commands/${id}/ack`, { ackVia: 'web_ui' }); load(); }
    finally { setAcking(null); }
  };

  const handleMarkExecuting = async (id: string) => {
    setAcking(id);
    try { await api.post(`/sko/commands/${id}/mark-executing`, { execMethod: 'manual_at_site' }); load(); }
    finally { setAcking(null); }
  };

  const handleComplete = async (id: string) => {
    setAcking(id);
    try { await api.post(`/sko/commands/${id}/complete`, {}); load(); }
    finally { setAcking(null); }
  };

  const STATUS_PILL: Record<string, { bg: string; text: string; label: string }> = {
    pending:      { bg: 'bg-[color-mix(in_srgb,var(--warn)_14%,transparent)]', text: 'text-[var(--warn)]', label: 'PENDING' },
    acknowledged: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'ACKED' },
    executing:    { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'EXECUTING' },
    completed:    { bg: 'bg-green-100',  text: 'text-green-700',  label: 'COMPLETED' },
    expired:      { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'EXPIRED' },
    rejected:     { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'REJECTED' },
    cancelled:    { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'CANCELLED' },
  };

  return (
    <div className="bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-white/10 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-semibold" style={{ color: textMain }}>
          {t('sko_inbox_full_title')} ({items.length})
        </h3>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5">
            <option value="all">{t('sko_inbox_filter_all')}</option>
            <option value="pending">Pending</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="executing">Executing</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-sm border border-gray-200 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 rounded px-2 py-1.5">
            <option value={7}>7 {t('ueex_page_days')}</option>
            <option value={30}>30 {t('ueex_page_days')}</option>
            <option value={90}>90 {t('ueex_page_days')}</option>
          </select>
        </div>
      </div>

      {error && <div className="rounded p-2 mb-2 text-sm bg-[color-mix(in_srgb,var(--err)_10%,transparent)] border border-[color-mix(in_srgb,var(--err)_30%,transparent)] text-[var(--err)]">{error}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-white/5">
            <tr className="border-b border-gray-200 dark:border-white/10 text-left">
              <th className="py-2 px-2 font-medium text-gray-600">Час</th>
              <th className="py-2 px-2 font-medium text-gray-600">Тип</th>
              <th className="py-2 px-2 font-medium text-gray-600">СЕС</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">Обмеження</th>
              <th className="py-2 px-2 font-medium text-gray-600">Період</th>
              <th className="py-2 px-2 font-medium text-gray-600">Причина</th>
              <th className="py-2 px-2 font-medium text-gray-600">Диспетчер</th>
              <th className="py-2 px-2 font-medium text-gray-600">Статус</th>
              <th className="py-2 px-2 font-medium text-gray-600">Дії</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9} className="py-6 text-center text-sm text-gray-400">{t('sko_no_data')}</td></tr>
            )}
            {items.map((c) => {
              const pill = STATUS_PILL[c.status] || STATUS_PILL.pending;
              return (
                <tr key={c.id} className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5">
                  <td className="py-2 px-2 font-mono text-[11px]">{fmtDateTime(c.receivedAt)}</td>
                  <td className="py-2 px-2 text-xs">
                    {c.commandType}
                    {c.severity === 'critical' && <span className="ml-1 text-[var(--err)]">!!!</span>}
                  </td>
                  <td className="py-2 px-2 text-xs">{c.assetName || '—'}</td>
                  <td className="py-2 px-2 text-right font-mono text-xs">
                    {c.curtailmentPct !== null ? `${c.curtailmentPct}%` :
                     c.targetKw !== null ? `${c.targetKw} kW` : '—'}
                  </td>
                  <td className="py-2 px-2 font-mono text-[11px]">
                    {c.effectiveFrom ? `${fmtDateTime(c.effectiveFrom).slice(11)}–${fmtDateTime(c.effectiveUntil).slice(11)}` : '—'}
                  </td>
                  <td className="py-2 px-2 text-xs truncate max-w-xs" title={c.reason || ''}>{c.reason || '—'}</td>
                  <td className="py-2 px-2 text-xs">{c.dispatcherName || '—'}</td>
                  <td className="py-2 px-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${pill.bg} ${pill.text}`}>{pill.label}</span>
                    {c.status === 'completed' && c.actualCompliancePct !== null && (
                      <div className="text-[10px] text-gray-500 mt-0.5">{c.actualCompliancePct}% comply</div>
                    )}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    {c.status === 'pending' && (
                      <button type="button" disabled={acking === c.id}
                        onClick={() => handleAck(c.id)}
                        className="text-[11px] px-2 py-1 rounded cursor-pointer disabled:opacity-50"
                        style={{ background: '#10B981', color: 'white' }}>
                        ✓ ACK
                      </button>
                    )}
                    {c.status === 'acknowledged' && (
                      <button type="button" disabled={acking === c.id}
                        onClick={() => handleMarkExecuting(c.id)}
                        className="text-[11px] px-2 py-1 rounded cursor-pointer disabled:opacity-50"
                        style={{ background: '#F59E0B', color: 'white' }}>
                        ▶ Виконання
                      </button>
                    )}
                    {c.status === 'executing' && (
                      <button type="button" disabled={acking === c.id}
                        onClick={() => handleComplete(c.id)}
                        className="text-[11px] px-2 py-1 rounded cursor-pointer disabled:opacity-50"
                        style={{ background: '#0066FF', color: 'white' }}>
                        ✓ Завершено
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
